import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import ScrollToTop from "@/components/ScrollToTop";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index.tsx";

const About = lazy(() => import("./pages/About.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const DentalAssociates = lazy(() => import("./pages/DentalAssociates.tsx"));
const DentalTherapists = lazy(() => import("./pages/DentalTherapists.tsx"));
const DentalHygienists = lazy(() => import("./pages/DentalHygienists.tsx"));
const PracticeOwners = lazy(() => import("./pages/PracticeOwners.tsx"));
const Book = lazy(() => import("./pages/Book.tsx"));
const WhatsAppButton = lazy(() => import("./components/WhatsAppButton.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const DentalBookkeeping = lazy(() => import("./pages/DentalBookkeeping.tsx"));
const DentalPracticeAccounting = lazy(() => import("./pages/DentalPracticeAccounting.tsx"));
const CityPage = lazy(() => import("./pages/CityPage.tsx"));
const DentistAccountants = lazy(() => import("./pages/DentistAccountants.tsx"));
const BuyingADentalPractice = lazy(() => import("./pages/BuyingADentalPractice.tsx"));
const LearningPillarPage = lazy(() => import("./pages/LearningPillarPage.tsx"));
const LearningCenterIndex = lazy(() => import("./pages/LearningCenterIndex.tsx"));
const LearningIntentPage = lazy(() => import("./pages/LearningIntentPage.tsx"));
const LimitedCompanyVsSoleTraderGuide = lazy(() => import("./pages/LimitedCompanyVsSoleTraderGuide.tsx"));
const DentalPracticeLoansGuide = lazy(() => import("./pages/DentalPracticeLoansGuide.tsx"));
const SellingADentalPractice = lazy(() => import("./pages/SellingADentalPractice.tsx"));
const MakingTaxDigitalGuide = lazy(() => import("./pages/MakingTaxDigitalGuide.tsx"));
const DentalPracticeMarketingGuide = lazy(() => import("./pages/DentalPracticeMarketingGuide.tsx"));
const DentalWorkingCapitalGuide = lazy(() => import("./pages/DentalWorkingCapitalGuide.tsx"));

const queryClient = new QueryClient();

// Branded full-screen loader matching the dark hero gradient so route
// transitions never flash white.
const RouteFallback = () => (
  <div
    role="status"
    aria-label="Loading page"
    className="min-h-screen flex items-center justify-center"
    style={{
      background: "linear-gradient(135deg, hsl(216 55% 10%), hsl(216 50% 16%), hsl(216 45% 20%))",
    }}
  >
    <div
      className="h-12 w-12 rounded-full border-2 border-white/20 border-t-white/90 animate-spin"
      aria-hidden="true"
    />
    <span className="sr-only">Loading</span>
  </div>
);

// Prefetch the most common route chunks once the browser is idle.
const prefetchCommonRoutes = () => {
  // Touch the dynamic imports so Vite fetches the chunks in the background.
  void import("./pages/About.tsx");
  void import("./pages/PracticeOwners.tsx");
  void import("./pages/DentalAssociates.tsx");
  void import("./pages/Book.tsx");
  void import("./pages/Blog.tsx");
};

const RoutePrefetcher = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Only prefetch from the homepage where users most often navigate from.
    if (pathname !== "/") return;
    if (typeof window === "undefined") return;

    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(prefetchCommonRoutes, { timeout: 4000 });
      return () => w.cancelIdleCallback?.(id);
    }

    const timer = window.setTimeout(prefetchCommonRoutes, 2500);
    return () => window.clearTimeout(timer);
  }, [pathname]);

  return null;
};

const DeferredFloatingWidgets = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    let id: number | undefined;
    let t: number | undefined;
    const start = () => {
      if (w.requestIdleCallback) {
        id = w.requestIdleCallback(() => setReady(true), { timeout: 4000 });
      } else {
        t = window.setTimeout(() => setReady(true), 3000);
      }
    };
    // Wait until after the load event so widgets never compete with LCP.
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    return () => {
      if (id !== undefined) (window as any).cancelIdleCallback?.(id);
      if (t !== undefined) window.clearTimeout(t);
    };
  }, []);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <WhatsAppButton />
    </Suspense>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <RoutePrefetcher />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/about" element={<About />} />
              <Route path="/dental-associates" element={<DentalAssociates />} />
              <Route path="/dental-therapists" element={<DentalTherapists />} />
              <Route path="/dental-hygienists" element={<DentalHygienists />} />
              <Route path="/practice-owners" element={<PracticeOwners />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/book" element={<Book />} />
              <Route path="/dental-bookkeeping" element={<DentalBookkeeping />} />
              <Route path="/dental-practice-accounting" element={<DentalPracticeAccounting />} />
              <Route path="/dentist-accountants" element={<DentistAccountants />} />
              <Route path="/dentist-accountants/:city" element={<CityPage />} />
              <Route path="/buying-a-dental-practice" element={<BuyingADentalPractice />} />
              <Route path="/learning-center" element={<LearningCenterIndex />} />
              <Route path="/learning-center/intent/:intent" element={<LearningIntentPage />} />
              <Route
                path="/learning-center/limited-company-vs-sole-trader-for-dentists"
                element={<LimitedCompanyVsSoleTraderGuide />}
              />
              <Route
                path="/learning-center/dental-practice-loans-guide"
                element={<DentalPracticeLoansGuide />}
              />
              <Route
                path="/selling-a-dental-practice"
                element={<SellingADentalPractice />}
              />
              <Route
                path="/learning-center/making-tax-digital-for-dental-practices"
                element={<MakingTaxDigitalGuide />}
              />
              <Route
                path="/learning-center/dental-practice-marketing-guide"
                element={<DentalPracticeMarketingGuide />}
              />
              <Route
                path="/learning-center/dental-practice-working-capital-guide"
                element={<DentalWorkingCapitalGuide />}
              />
              <Route path="/learning-center/:pillar" element={<LearningPillarPage />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <DeferredFloatingWidgets />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
